import { API_URL } from './urlConfig';

/**
 * Cliente HTTP da aplicação.
 *
 * Existe por causa de uma única linha: `credentials: 'include'`. A sessão vive
 * num cookie httpOnly, que o JavaScript não enxerga e portanto não consegue
 * anexar à mão — quem anexa é o navegador, e só quando a requisição pede
 * explicitamente para enviar credenciais. Um `fetch` cru omite o cookie em
 * chamadas para outra origem (silentpuma.com → api.silentpuma.com) e a resposta
 * volta 401 sem explicação óbvia.
 *
 * Concentrar isso aqui é o que evita que a próxima chamada nova esqueça.
 */
/**
 * Idioma da interface, para o servidor responder no mesmo.
 *
 * Lê do `localStorage` e não do contexto do React porque `apiFetch` é uma
 * função comum, chamada de dentro e de fora de componente. O provedor de idioma
 * mantém essa chave gravada a cada troca, então o valor aqui é o que está na
 * tela naquele instante.
 *
 * `Accept-Language` e não um cabeçalho nosso: com valor simples ele é da lista
 * segura do CORS, então não acrescenta requisição de preflight a cada chamada.
 * E é o cabeçalho que qualquer cliente da API já saberia mandar.
 */
const idiomaAtual = () => {
  try {
    return window.localStorage.getItem('silentpuma:idioma') || 'en';
  } catch {
    return 'en';
  }
};

export const apiFetch = (caminho, opcoes = {}) =>
  fetch(caminho.startsWith('http') ? caminho : `${API_URL}${caminho}`, {
    ...opcoes,
    headers: { 'Accept-Language': idiomaAtual(), ...opcoes.headers },
    credentials: 'include'
  });

/**
 * Encerra a sessão no servidor.
 *
 * Limpar estado local não basta mais: o cookie é do servidor, e só uma resposta
 * com `Set-Cookie` de remoção o apaga. Sem esta chamada o usuário sairia da
 * interface mas continuaria autenticado na API.
 *
 * Nunca lança — um logout que falha por rede ainda precisa deixar a interface
 * sair; o cookie expira em 7 dias de qualquer forma.
 */
export const logoutRequest = async () => {
  try {
    await apiFetch('/auth/logout', { method: 'POST' });
  } catch {
    // Sem rede: segue o logout local.
  }
};

/**
 * Recupera a sessão atual a partir do cookie.
 *
 * Devolve o usuário quando há sessão válida e `null` quando não há. É o que
 * substitui a leitura do `localStorage` no boot: com o token inacessível ao
 * JavaScript, perguntar ao servidor é a única forma de saber se há sessão — e
 * de quebra os dados vêm sempre frescos, sem cache local desatualizado.
 */
export const fetchSession = async () => {
  try {
    const resposta = await apiFetch('/me');
    if (!resposta.ok) return null;
    return await resposta.json();
  } catch {
    return null;
  }
};
